import ReactGA from 'react-ga4';
import { parsePrice } from './offers';

// Google Analytics 4. Uses GA4's standard shop events (view_item, add_to_cart, begin_checkout,
// purchase...) so the Monetization reports fill in: revenue, best-selling products, and where
// customers drop off between product page, cart, checkout and order.
//
// Page views are sent by the app, one per page, after the page has set its title. GA's own
// "page changes based on browser history events" should be switched off in GA (Admin > Data
// streams > Enhanced measurement > Page views > advanced settings), or navigations count twice.
const CURRENCY = 'USD';
let enabled = false;

const isLocalhost = () => /^(localhost|127\.|0\.0\.0\.0)/.test(window.location.hostname);

export const initGA = (measurementId) => {
  if (!measurementId || measurementId === 'G-XXXXXXXXXX' || isLocalhost()) return;
  // No automatic page view on load: trackPageView sends exactly one per page
  ReactGA.initialize(measurementId, { gtagOptions: { send_page_view: false } });
  enabled = true;
};

const send = (name, params) => {
  if (enabled) ReactGA.event(name, params);
};

export const trackPageView = (path, title) => {
  if (enabled) ReactGA.send({ hitType: 'pageview', page: path, title });
};

// GA item from a product or cart item
const toItem = (product, quantity = product.quantity || 1, price = parsePrice(product.price)) => ({
  item_id: String(product.id),
  item_name: product.name,
  item_category: product.categories?.name || undefined,
  price,
  quantity,
});

const round2 = (value) => Math.round(value * 100) / 100;

export const trackViewItem = (product) => {
  const price = parsePrice(product.price);
  send('view_item', { currency: CURRENCY, value: price, items: [toItem(product, 1, price)] });
};

export const trackAddToCart = (product, quantity = 1) => {
  const price = parsePrice(product.price);
  send('add_to_cart', { currency: CURRENCY, value: round2(price * quantity), items: [toItem(product, quantity, price)] });
};

export const trackRemoveFromCart = (item) => {
  const price = parsePrice(item.price);
  send('remove_from_cart', { currency: CURRENCY, value: round2(price * item.quantity), items: [toItem(item, item.quantity, price)] });
};

// `lines` from priceCartLines (prices after offers)
const lineItems = (lines) => lines.map((line) => ({
  ...toItem(line.item, line.quantity, line.quantity ? round2(line.total / line.quantity) : line.unitPrice),
  ...(line.discount > 0 ? { discount: round2(line.discount / line.quantity) } : {}),
}));

export const trackViewCart = (lines, subtotal) => {
  send('view_cart', { currency: CURRENCY, value: subtotal, items: lineItems(lines) });
};

export const trackBeginCheckout = (lines, subtotal, coupon) => {
  send('begin_checkout', { currency: CURRENCY, value: subtotal, coupon: coupon || undefined, items: lineItems(lines) });
};

// Once, right after the order is saved. `order` is the saved row (its total is the database's).
export const trackPurchase = ({ order, lines, shipping, channel }) => {
  send('purchase', {
    transaction_id: order.order_number,
    currency: CURRENCY,
    value: Number(order.total_amount) || 0,
    shipping: shipping || 0,
    coupon: order.coupon_code || undefined,
    payment_type: channel === 'whatsapp' ? 'WhatsApp order' : 'Cash on delivery',
    items: lineItems(lines),
  });
};

export const trackSignUp = () => send('sign_up', { method: 'email' });
export const trackLogin = () => send('login', { method: 'email' });
